import { pipe, Option, Array as A } from 'effect';
import moment from 'moment';
import { extractEmail } from '@/lib/helpers';

import type { WorkspaceSearchQuery } from '@/pages/company/WorkspaceRoute/components/WorkspaceSearch/querySchema';

export type QueryChipType = 'text' | 'date' | 'invoice_date' | 'payment_date' | 'label';

export interface QueryChip {
  readonly type: QueryChipType;
  readonly value: string;
}

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';
const DATE_TYPES = ['date', 'invoice_date', 'payment_date'] as const;

// A date filter is stored as a [from, to] pair; render it as a single "from - to"
// chip — faithful to the legacy FavoriteTagsPanel `dateParser` (format each end
// with the company date format, join with " - ").
const dateChip = (
  query: WorkspaceSearchQuery,
  type: (typeof DATE_TYPES)[number],
  dateFormat: string,
): Option.Option<QueryChip> =>
  pipe(
    Option.fromNullable(query[type]),
    Option.filter((values) => values.length > 0),
    Option.map((values) => ({
      type,
      value: values.map((v) => moment(v).format(dateFormat || DEFAULT_DATE_FORMAT)).join(' - '),
    })),
  );

// `label` is a system user attribute carried by saved Favorites; its display value
// prefers an email parsed out of the raw `query` expression, else the label itself.
const labelChip = (query: WorkspaceSearchQuery): Option.Option<QueryChip> =>
  pipe(
    Option.fromNullable(query.label),
    Option.map((label) => {
      const email = pipe(
        Option.fromNullable(query.query),
        Option.flatMap((raw) => Option.fromNullable(extractEmail(raw.split(' ').join('+')))),
        Option.getOrNull,
      );
      return { type: 'label' as const, value: email ?? label };
    }),
  );

// Query-derived chips, in a stable [text, date-types, label] order. Mirrors the
// legacy `currentTags` derivation but pure and origin-tagged so each chip's
// removal can be routed back to its own URL-query key.
export const deriveQueryChips = (query: WorkspaceSearchQuery, dateFormat = DEFAULT_DATE_FORMAT): QueryChip[] => {
  const textChips = pipe(
    Option.fromNullable(query.text),
    Option.getOrElse((): string[] => []),
    A.map((value): QueryChip => ({ type: 'text', value })),
  );
  const dateChips = pipe(
    DATE_TYPES,
    A.filterMap((type) => dateChip(query, type, dateFormat)),
  );
  const label = pipe(
    labelChip(query),
    Option.match({ onNone: (): QueryChip[] => [], onSome: (chip) => [chip] }),
  );
  return [...textChips, ...dateChips, ...label];
};

export interface OverflowSplit<T> {
  readonly visible: T[];
  readonly hidden: T[];
}

// The first `maxVisible` chips stay inline, the rest collapse behind the "+N" control.
// `maxVisible` comes from `fitVisibleCount` below.
// NB: a generic `function` declaration, not a `<T>(…) =>` arrow — the esbuild-loader
// parses every file (incl. .ts) in `tsx` mode, where a bare generic arrow reads as a
// JSX tag.
export function splitOverflow<T>(chips: readonly T[], maxVisible: number): OverflowSplit<T> {
  return pipe(chips, A.splitAt(maxVisible), ([visible, hidden]) => ({ visible, hidden }));
}

export interface FitBudget {
  readonly available: number; // stable width the chip lane may use
  readonly countReserve: number; // width the "+N" counter needs when anything is hidden
  readonly gap: number;
}

// How many chips fit, decided from their own intrinsic widths against a STABLE available width —
// never from the space left over after the chips render, which oscillates by construction (show a
// chip → less free space → hide it → more free space → show it; da-19657 design.md → Risks).
//
// Two passes: if every chip fits with no counter at all, all of them are visible; otherwise the
// counter's width is reserved and the pass repeats. Every input is independent of the result, so
// this is a pure function of stable values and cannot feed back into itself.
export const fitVisibleCount = (widths: readonly number[], budget: FitBudget): number => {
  const fitting = (available: number) =>
    pipe(
      widths,
      A.reduce({ used: 0, count: 0, full: false }, (acc, width) => {
        if (acc.full) return acc;
        const used = acc.used + width + (acc.count > 0 ? budget.gap : 0);
        return used <= available ? { used, count: acc.count + 1, full: false } : { ...acc, full: true };
      }),
      ({ count }) => count,
    );

  const withoutCounter = fitting(budget.available);
  return withoutCounter === widths.length
    ? withoutCounter
    : fitting(budget.available - budget.countReserve - budget.gap);
};
